import clsx from 'clsx';
import { ReactNode } from 'react';

import { Skeleton } from '../skeleton';
import { Icon, IconProps } from '../icon';
import { Popover, PopoverProps } from '../popover';

import type { MenuItemProps } from '../menu-item';

import { getRandomItem } from '../../utils/helper-utils';

import './single-select.scss';

export const SingleSelect = (props: SingleSelectProps) => {
  const {
    options,
    loading,
    disabled,
    iconSize,
    maxWidth,
    className,
    arrowSize,
    placeholder,
    popoverProps,
    multiColorIcon,
    showName = true,
    showIcon = true,
    showArrow = true,
    appearance = 'default',
    ...rest
  } = props;

  const size = iconSize || 16;
  const notDefault = appearance !== 'default';
  const selectedOption = options.find((el) => el.selected)!;

  const renderSkeleton = () => (
    <>
      <Skeleton trueWidth={size} trueHeight={size} width={14} height={14} />
      <Skeleton width={notDefault ? 48 : getRandomItem([60, 50])} height={notDefault ? 4 : 6} />
      {notDefault && <Skeleton trueWidth={14} trueHeight={14} width={8} height={8} borderRadius={3} />}
    </>
  );

  const renderClickableText = () => {
    if (loading) {
      return renderSkeleton();
    }

    if (placeholder && !selectedOption) {
      return placeholder;
    }

    return (
      <>
        {showIcon && selectedOption?.icon && (
          <Icon
            size={size}
            type={selectedOption?.icon!}
            multiColor={multiColorIcon}
            className={clsx({ 'extra-margin': !iconSize })}
          />
        )}
        {(showName || showArrow) && (
          <div>
            {showName && <span className="select-preview">{selectedOption?.displayName || selectedOption?.name}</span>}
            {showArrow && (
              <div className="icon-holder">
                <Icon size={arrowSize ?? 16} type="arrow-down-01" />
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <Popover menu={options} placement="bottom" maxWidth={maxWidth} {...popoverProps}>
      <div
        className={clsx('single-select-display', `a-${appearance}`, className, {
          'pointer-events-none': loading || disabled,
          disabled,
        })}
        {...rest}
      >
        {renderClickableText()}
      </div>
    </Popover>
  );
};

export interface SingleSelectProps {
  loading?: boolean;
  disabled?: boolean;
  showIcon?: boolean;
  showName?: boolean;
  className?: string;
  showArrow?: boolean;
  arrowSize?: number;
  multiColorIcon?: boolean;
  placeholder?: ReactNode;
  popoverProps?: PopoverProps;
  iconSize?: IconProps['size'];
  maxWidth?: PopoverProps['maxWidth'];
  appearance?: 'default' | 'light' | 'greyscale' | 'action-bar';
  options: (MenuItemProps & { displayName?: string })[];
}
